import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sendAccessCode, sendSmsAccessCode, loginWithCode } from '../../services/authService';
import { formatPhoneInput, toE164, isValidPhone } from '../../utils/phoneNumber';
import { Modal } from '../ui/Modal';

interface AccessCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
  mode: 'email' | 'sms';
  initialEmail?: string;
  initialPhone?: string;
  stayLoggedIn: boolean;
}

export const AccessCodeModal: React.FC<AccessCodeModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  mode,
  initialEmail = '',
  initialPhone = '',
  stayLoggedIn,
}) => {
  const { t } = useTranslation('auth');
  const [step, setStep] = useState<'send' | 'verify'>('send');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('send');
      setIdentifier(mode === 'email' ? initialEmail : initialPhone);
      setCode('');
      setError('');
      setSending(false);
      setVerifying(false);
    }
  }, [isOpen, mode, initialEmail, initialPhone]);

  const isEmailMode = mode === 'email';

  const isSendDisabled = sending || (isEmailMode ? !identifier : !isValidPhone(identifier));

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      if (isEmailMode) {
        await sendAccessCode(identifier);
      } else {
        await sendSmsAccessCode(toE164(identifier)!);
      }
      setStep('verify');
    } catch {
      setError(isEmailMode ? t('accessCode.errorSendEmail') : t('accessCode.errorSendSms'));
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const id = isEmailMode ? { email: identifier } : { phone: toE164(identifier)! };
      await loginWithCode(id, code, stayLoggedIn);
      onLoginSuccess();
    } catch (err: unknown) {
      const status = (err as Error & { status?: number }).status;
      if (status === 410) {
        setError(t('accessCode.errorExpired'));
        setCode('');
        setStep('send');
      } else {
        setError(t('accessCode.errorInvalid'));
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-50">
      {step === 'send' ? (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">
              {isEmailMode ? t('accessCode.titleEmail') : t('accessCode.titleSms')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {isEmailMode ? t('accessCode.bodyEmailSend') : t('accessCode.bodySmsSend')}
            </p>
            <form onSubmit={handleSendCode} className="flex flex-col gap-4">
              {isEmailMode ? (
                <input
                  type="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('accessCode.placeholderEmail')}
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              ) : (
                <input
                  type="tel"
                  inputMode="tel"
                  value={identifier}
                  onChange={(e) => setIdentifier(formatPhoneInput(e.target.value))}
                  placeholder={t('accessCode.placeholderPhone')}
                  autoFocus
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              )}
              {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSendDisabled}
                  data-testid="access-code-send"
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? t('accessCode.sending') : t('accessCode.sendCta')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">{t('accessCode.titleVerify')}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {isEmailMode ? t('accessCode.bodyEmailVerify') : t('accessCode.bodySmsVerify')}
            </p>
            <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                data-testid="access-code-input"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-center text-2xl tracking-widest"
                autoFocus
              />
              {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={verifying || code.length !== 6}
                  data-testid="access-code-submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {verifying ? t('accessCode.loggingIn') : t('accessCode.loginCta')}
                </button>
              </div>
            </form>
          </>
        )}
    </Modal>
  );
};
